package com.flowyride.feature.planner.ui.components

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.unit.dp
import com.flowyride.core.designsystem.component.FlowyTextField
import com.flowyride.core.domain.favorite.FavoriteRules
import com.flowyride.feature.planner.GpxQuestion
import com.flowyride.feature.planner.resources.Res
import com.flowyride.feature.planner.resources.cancel
import com.flowyride.feature.planner.resources.favorite_name_label
import com.flowyride.feature.planner.resources.favorite_save
import com.flowyride.feature.planner.resources.gpx_choose_track_message
import com.flowyride.feature.planner.resources.gpx_choose_track_title
import com.flowyride.feature.planner.resources.gpx_points_confirm
import com.flowyride.feature.planner.resources.gpx_points_message
import com.flowyride.feature.planner.resources.gpx_points_title
import com.flowyride.feature.planner.resources.replace_confirm
import com.flowyride.feature.planner.resources.replace_message
import com.flowyride.feature.planner.resources.replace_title
import com.flowyride.feature.planner.resources.route_summary
import com.flowyride.feature.planner.ui.formatDistance
import com.flowyride.feature.planner.ui.formatDuration
import com.flowyride.core.domain.usecase.ImportedTrack
import com.flowyride.core.domain.place.SavedPlaceRules
import org.jetbrains.compose.resources.stringResource

/** Asks before a GPX file or a favorite wipes the ride on screen. */
@Composable
internal fun ReplaceRideDialog(onConfirm: () -> Unit, onDismiss: () -> Unit) {
    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text(stringResource(Res.string.replace_title)) },
        text = { Text(stringResource(Res.string.replace_message)) },
        confirmButton = { TextButton(onClick = onConfirm) { Text(stringResource(Res.string.replace_confirm)) } },
        dismissButton = { TextButton(onClick = onDismiss) { Text(stringResource(Res.string.cancel)) } },
    )
}

/** Name of a new favorite, or the new name of an existing one. */
@Composable
internal fun FavoriteNameDialog(
    title: String,
    initialName: String,
    confirmLabel: String,
    onConfirm: (String) -> Unit,
    onDismiss: () -> Unit,
    maxLength: Int = FavoriteRules.NAME_MAX,
) {
    var name by remember { mutableStateOf(initialName.take(maxLength)) }
    val valid = name.isNotBlank() && name.trim().length <= maxLength
    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text(title) },
        text = {
            FlowyTextField(
                value = name,
                onValueChange = { name = it.take(maxLength) },
                label = stringResource(Res.string.favorite_name_label),
                imeAction = ImeAction.Done,
                onImeAction = { if (valid) onConfirm(name.trim()) },
                modifier = Modifier.fillMaxWidth(),
            )
        },
        confirmButton = { TextButton(onClick = { onConfirm(name.trim()) }, enabled = valid) { Text(confirmLabel) } },
        dismissButton = { TextButton(onClick = onDismiss) { Text(stringResource(Res.string.cancel)) } },
    )
}

/** Name of a saved address ("Maison"). */
@Composable
internal fun PlaceNameDialog(
    title: String,
    initialName: String,
    confirmLabel: String,
    onConfirm: (String) -> Unit,
    onDismiss: () -> Unit,
) {
    FavoriteNameDialog(title, initialName, confirmLabel, onConfirm, onDismiss, maxLength = SavedPlaceRules.NAME_MAX)
}

@Composable
internal fun SaveFavoriteDialog(initialName: String, title: String, onSave: (String) -> Unit, onDismiss: () -> Unit) {
    FavoriteNameDialog(title, initialName, stringResource(Res.string.favorite_save), onSave, onDismiss)
}

/** What to do with an imported file that is not a single line. */
@Composable
internal fun GpxQuestionDialog(
    question: GpxQuestion,
    onTrackChosen: (ImportedTrack) -> Unit,
    onComputeConfirmed: () -> Unit,
    onDismiss: () -> Unit,
) {
    when (question) {
        is GpxQuestion.ChooseTrack -> AlertDialog(
            onDismissRequest = onDismiss,
            title = { Text(stringResource(Res.string.gpx_choose_track_title)) },
            text = {
                Column {
                    Text(stringResource(Res.string.gpx_choose_track_message), style = MaterialTheme.typography.bodyMedium)
                    question.tracks.forEach { track ->
                        HorizontalDivider(Modifier.padding(top = 8.dp))
                        Column(
                            Modifier.fillMaxWidth().clickable { onTrackChosen(track) }.padding(vertical = 10.dp),
                            verticalArrangement = Arrangement.spacedBy(2.dp),
                        ) {
                            Text(track.name, style = MaterialTheme.typography.bodyLarge)
                            Text(
                                stringResource(
                                    Res.string.route_summary,
                                    formatDistance(track.route.distanceMeters),
                                    formatDuration(track.route.durationSeconds),
                                ),
                                style = MaterialTheme.typography.bodySmall,
                                color = MaterialTheme.colorScheme.onSurfaceVariant,
                            )
                        }
                    }
                }
            },
            confirmButton = {},
            dismissButton = { TextButton(onClick = onDismiss) { Text(stringResource(Res.string.cancel)) } },
        )
        is GpxQuestion.ComputeFromPoints -> AlertDialog(
            onDismissRequest = onDismiss,
            title = { Text(stringResource(Res.string.gpx_points_title)) },
            text = { Text(stringResource(Res.string.gpx_points_message, question.content.points.size)) },
            confirmButton = { TextButton(onClick = onComputeConfirmed) { Text(stringResource(Res.string.gpx_points_confirm)) } },
            dismissButton = { TextButton(onClick = onDismiss) { Text(stringResource(Res.string.cancel)) } },
        )
    }
}
